/**
 * Compress images (dimension + JPEG quality) until under a byte limit.
 * Dimension pass first, then quality pass; PNG inputs can fall back to JPEG if needed.
 * SVG inputs are rasterized (profile path outputs JPEG) so uploads stay JPEG/PNG-only server-side.
 * Keep maxBytes in sync with auth-client MAX_PHOTO_BYTES / MAX_AD_IMAGE_BYTES (5 MB).
 */

const FIVE_MB = 5 * 1024 * 1024;

function isSvgFile(file: File): boolean {
  const t = (file.type || "").toLowerCase();
  if (t === "image/svg+xml") return true;
  return /\.svg$/i.test(file.name);
}

export type CompressImageUnderMaxBytesOptions = {
  maxBytes: number;
  initialMaxSide: number;
  minMaxSide: number;
  initialQuality: number;
  minQuality: number;
  maxAttempts: number;
  /** When input is PNG, try PNG output first (transparency); fall back to JPEG if still too large */
  preferPngWhenInputIsPng?: boolean;
  baseName?: string;
};

export type CompressImageResult =
  | { ok: true; file: File }
  | { ok: false; error: string };

function scaleToMaxSide(nw: number, nh: number, maxSide: number): { w: number; h: number } {
  if (nw <= maxSide && nh <= maxSide) return { w: nw, h: nh };
  const s = Math.min(maxSide / nw, maxSide / nh);
  return { w: Math.round(nw * s), h: Math.round(nh * s) };
}

function encodeToBlob(
  img: HTMLImageElement,
  maxSide: number,
  mimeOut: "image/jpeg" | "image/png",
  jpegQuality: number
): Promise<Blob | null> {
  const nw = img.naturalWidth || img.width;
  const nh = img.naturalHeight || img.height;
  const { w, h } = scaleToMaxSide(nw, nh, maxSide);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.resolve(null);
  ctx.drawImage(img, 0, 0, w, h);
  return new Promise((resolve) => {
    canvas.toBlob(
      (b) => resolve(b),
      mimeOut,
      mimeOut === "image/jpeg" ? jpegQuality : undefined
    );
  });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const el = document.createElement("img");
    const url = URL.createObjectURL(file);
    el.onload = () => {
      URL.revokeObjectURL(url);
      resolve(el);
    };
    el.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Não foi possível carregar a imagem."));
    };
    el.src = url;
  });
}

/**
 * Resize/re-encode until output is ≤ maxBytes or limits are exhausted.
 */
export async function compressImageUnderMaxBytes(
  file: File,
  options: CompressImageUnderMaxBytesOptions
): Promise<CompressImageResult> {
  const {
    maxBytes,
    initialMaxSide,
    minMaxSide,
    initialQuality,
    minQuality,
    maxAttempts,
    preferPngWhenInputIsPng = false,
    baseName,
  } = options;

  let img: HTMLImageElement;
  try {
    img = await loadImage(file);
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "Erro ao carregar a imagem." };
  }

  let nw = img.naturalWidth || img.width;
  let nh = img.naturalHeight || img.height;
  if ((nw <= 0 || nh <= 0) && isSvgFile(file)) {
    img.width = initialMaxSide;
    img.height = initialMaxSide;
    nw = initialMaxSide;
    nh = initialMaxSide;
  }
  if (nw <= 0 || nh <= 0) {
    return { ok: false, error: "Imagem inválida." };
  }

  const stem = baseName ?? (file.name.replace(/\.[^.]+$/i, "") || "image");
  const isPngInput = (file.type || "").toLowerCase() === "image/png";

  let maxSide = initialMaxSide;
  let quality = initialQuality;
  let outputPng = Boolean(preferPngWhenInputIsPng && isPngInput);
  let lastBlobSize = -1;
  let stuckCount = 0;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const mimeOut: "image/jpeg" | "image/png" = outputPng ? "image/png" : "image/jpeg";
    const ext = outputPng ? ".png" : ".jpg";
    const blob = await encodeToBlob(img, maxSide, mimeOut, quality);
    if (!blob) {
      return { ok: false, error: "Não foi possível processar a imagem." };
    }

    if (blob.size <= maxBytes) {
      const outFile = new File([blob], `${stem}${ext}`, {
        type: mimeOut,
        lastModified: Date.now(),
      });
      return { ok: true, file: outFile };
    }

    if (blob.size >= lastBlobSize) stuckCount++;
    else stuckCount = 0;
    lastBlobSize = blob.size;

    if (stuckCount >= 2) {
      if (outputPng) {
        outputPng = false;
        quality = initialQuality;
        maxSide = initialMaxSide;
        stuckCount = 0;
        lastBlobSize = -1;
        continue;
      }
      break;
    }

    if (maxSide > minMaxSide) {
      maxSide = Math.max(minMaxSide, Math.round(maxSide * 0.82));
      continue;
    }

    if (!outputPng && quality > minQuality) {
      quality = Math.max(minQuality, quality - 0.07);
      continue;
    }

    if (outputPng) {
      outputPng = false;
      quality = initialQuality;
      maxSide = initialMaxSide;
      lastBlobSize = -1;
      stuckCount = 0;
      continue;
    }

    break;
  }

  return {
    ok: false,
    error:
      "A imagem continua grande demais (máx. 5 MB) mesmo após comprimir. Tente outra foto ou reduza a resolução.",
  };
}

/**
 * Listing photos: same 5 MB cap as BFF; PNG kept when possible, then JPEG.
 *
 * initialMaxSide=1024 is a deliberate compromise: the ad-detail hero renders at
 * max-w-lg (~512 CSS px) which maps to ~1024 actual px at DPR 2 (most iPhones);
 * at DPR 3 it is ~35% under ideal but visually indistinguishable on typical
 * second-hand-item photos. Meanwhile feed cards (~180 CSS px) and thumbs are
 * ~3-6x oversized at 1200px, so dropping the cap to 1024 saves ~30% bandwidth
 * and bitmap memory across the whole app with no perceived quality loss.
 */
export async function compressAdImageForUpload(file: File): Promise<CompressImageResult> {
  return compressImageUnderMaxBytes(file, {
    maxBytes: FIVE_MB,
    initialMaxSide: 1024,
    minMaxSide: 480,
    initialQuality: 0.88,
    minQuality: 0.45,
    maxAttempts: 18,
    preferPngWhenInputIsPng: true,
    baseName: file.name.replace(/\.[^.]+$/i, "") || "image",
  });
}

/** Profile photo: always JPEG path; 512 → min side then quality (matches previous resize-profile intent). */
export async function compressProfilePhotoForUpload(file: File): Promise<CompressImageResult> {
  return compressImageUnderMaxBytes(file, {
    maxBytes: FIVE_MB,
    initialMaxSide: 512,
    minMaxSide: 320,
    initialQuality: 0.85,
    minQuality: 0.45,
    maxAttempts: 16,
    preferPngWhenInputIsPng: false,
    baseName: file.name.replace(/\.[^.]+$/i, "") || "photo",
  });
}
